// What a search hit is called, and where clicking it goes.
//
// Kept apart from the components so the dropdown and the results page cannot
// disagree about either — two labels for `term_line`, or a link that works in
// one place and not the other, is the kind of drift nobody notices.

import type { SearchHit, SearchHitKind } from "@crm/shared";

const LABELS: Record<SearchHitKind, string> = {
  deal: "Deals",
  partner: "Partners",
  agreement: "Agreements",
  invoice: "Invoices",
  term_line: "Term lines",
  tenant_binding: "Tenant bindings",
  note: "Notes",
};

export function hitLabel(kind: SearchHitKind): string {
  return LABELS[kind];
}

/** The singular, for a row that names its own kind. */
export function hitKindName(kind: SearchHitKind): string {
  const plural = LABELS[kind];
  return plural.endsWith("s") ? plural.slice(0, -1) : plural;
}

/**
 * Where a hit leads, or null when this app has no screen for it yet.
 *
 * Null rather than a dead link: a tenant binding is a real row with no screen,
 * and a result that navigates nowhere teaches somebody the search is broken.
 * The caller renders those as plain rows.
 */
export function hitTarget(hit: SearchHit): { to: string; search?: Record<string, string> } | null {
  switch (hit.kind) {
    case "deal":
      return { to: `/deals/${hit.id}` };
    case "partner":
      return { to: `/partners/${hit.id}` };
    case "invoice":
      return { to: `/invoices/${hit.id}` };
    // A term line and a note are both reached THROUGH their deal, which is
    // where a person can see them in context. An unfiled note has no deal, so
    // it goes to the inbox that exists for exactly that case.
    case "term_line":
      return hit.deal_id === null ? null : { to: `/deals/${hit.deal_id}` };
    case "note":
      return hit.deal_id === null
        ? { to: "/notes", search: { note: hit.id } }
        : { to: `/deals/${hit.deal_id}`, search: { note: hit.id } };
    // Agreements are reached through their partner, which the hit does not
    // carry; tenant bindings have no screen at all.
    case "agreement":
    case "tenant_binding":
      return null;
  }
}
